import * as $ from 'jquery';
import { disposableSubscription, serialDisposable } from 'common/barrels/core';
import { Observable, Subject } from 'common/barrels/rx';
import { Map } from 'immutable';
import { newGuid } from 'common/functions/guid';
import * as array from 'common/functions/array';
import { preserveScroll } from 'common/functions/scroll';

import { ContainerControl } from 'runtime/controls/containercontrol';
import { ControlArgs } from 'runtime/controls/controlargs';
import { registerBinding } from 'runtime/bindingengine';
import { applyBindings } from 'runtime/bindingengine';
import { ViewRequested } from 'runtime/events/viewrequested';
import { MethodRouter } from 'runtime/methodrouter';
import { RootContainer } from 'runtime/controls/rootcontainer';

import 'runtime/styles/controls/repeater.less';

export class Repeater extends ContainerControl {
    private valueContext: string;
    private valueBindingDisposable = serialDisposable(null);
    private renderedRowIds: string[] = [];
    private renderedRows = Map<string, JQuery>();

    private $container: JQuery;

    constructor(args: ControlArgs) {
        super(args);

        this.$element.addClass('control-container repeater');

        this.$container = $('<div />')
            .addClass('repeater-container')
            .addClass('repeater-container-' + this.config.orientation.toLowerCase())
            .appendTo(this.$element);

        this.applyTooltip(this.$element);

        this.methods.register(this.config.elementName, this);

        this.subscriptions = disposableSubscription(
            this.processBindings(this.config),
            this.managePrimaryBinding('value')
        );

        this.initialize();
    }

    value(value) {
        this.checkInitialValueReceived();

        this.valueContext = value;
        this.valueBindingDisposable.set(this.values.bindOneWay('Field', this.valueContext, 'Rows', (values, identifier) => this.rows(identifier, values)));
    }

    rows(fieldContext, values) {
        this.updateElements(values);
    }

    addNewRow() {
        this.values.update('Field', this.valueContext, 'Rows', this.renderedRowIds.concat(newGuid()));
    }

    private updateElements(rowIds: string[]) {
        array.except(this.renderedRowIds, ...rowIds).forEach(x => {
            this.renderedRows.get(x).remove();
            this.renderedRows = this.renderedRows.remove(x);
        });

        var store = (k, v) => { this.renderedRows = this.renderedRows.set(k, v); return v; };

        preserveScroll(
            () => rowIds.map(x => this.renderedRows.get(x) || store(x, this.buildElement(x))).forEach(x => this.$container.append(x)),
            this.$element
        );

        this.renderedRowIds = rowIds;
    }

    private buildElement(context: string): JQuery {
        const $elementContainer = $('<div />');
        
        const viewName = new Subject<string>();

        const subscription = viewName.distinctUntilChanged().subscribe(x => this.loadViewToElement($elementContainer, context, x));

        if (this.config.viewNameType === 'literal')
            viewName.next(this.config.viewName)
        else if (this.config.viewNameType === 'rule' || this.config.viewNameType === 'field') {
            const disposer = this.values.bindOneWay(this.config.viewNameType === 'rule' ? 'Rule' : 'Field', context, this.config.viewName, x => viewName.next(x));
            $elementContainer.on('remove', () => disposer.dispose());
        }
        else
            throw new Error('Unknown view name type!');

        return $elementContainer.owns(subscription);
    }

    private loadViewToElement($elementContainer: JQuery, context: string, viewName: string) {
        this.eventAggregator.publish(new ViewRequested(viewName, view => {
            var $view = $(view);
            $elementContainer.empty().append($view);
            const methods = new MethodRouter(this.methods, viewName);
            $view.owns(methods);
            applyBindings($view, context, this.eventAggregator, methods, this.values, this.resources, this, this.serializer);

            var rootContainer = <RootContainer>$view.data('control');
            $elementContainer.height(rootContainer.configuredHeight()).width(rootContainer.configuredWidth());
            rootContainer.activate();

            this.reapplyEnabledState();
        }));
    }

    applyEnabledState(value: boolean) {
        this.propagateEnabledStateToChildElements(this.$container.children(), this.writableAgnosticDerivedEnabledState());
    }

    protected applyScrollVetoBehavior() { }

    focus() {
        this.setFocusToFirstFocusableSubElement(this.$container);
    }

    resizeStream(): Observable<{}> {
        return Observable.never();
    }
}

registerBinding('repeater', Repeater);